/**
 * Centralized vehicle data constants
 * Extracted from RenewInfo.js to improve maintainability
 */

/**
 * Static product catalogue sourced from:
 *   POST https://api.prod.myagentpro.ng/api/v1/app/fixedagent/product/list
 * Last synced: 2026-07-23
 *
 * Key fields per product:
 *   id                – product ID sent as `product_id` to all order APIs
 *   pin_type          – vehicle class used for pin selection (e.g. "PrivateMotor")
 *   nicon_category_id – UUID required by the NICON order API
 *   nicon_category_name – Human-readable NICON category
 *   cei_policy_type   – Policy type string required by the CEI order API
 *   product_category  – Maps to the `apiName` on each static vehicle card
 *
 * Products WITHOUT a nicon_category_id (MotorCycle, TriCycle) do not have a
 * corresponding NICON product on the backend and should not be shown for NICON.
 */
export const NICON_PRODUCT_CATALOGUE = {
  /** Private Motor */
  PrivateMotor: {
    id: 1,
    product_name: "Private Motor - Third Party Insurance",
    product_price: 15000,
    product_category: "PrivateMotor",
    pin_type: "PrivateMotor",
    nicon_category_id: "98a98c65-0813-4d9b-a917-761c099254e6",
    nicon_category_name: "Private Motor",
    cei_policy_type: "PrivateMotor",
  },
  /** Commercial Vehicle */
  Commercial: {
    id: 9,
    product_name: "Commercial - Third Party Insurance",
    product_price: 20000,
    product_category: "Commercial",
    pin_type: "Commercial",
    nicon_category_id: "98a98c14-ec36-4978-bdc0-96f7ab146a96",
    nicon_category_name: "Commercial Vehicle",
    cei_policy_type: "Commercial",
  },
  /** Truck / Trailer */
  Truck: {
    id: 10,
    product_name: "Truck",
    product_price: 100000,
    product_category: "Truck",
    pin_type: "Truck",
    nicon_category_id: "98a98c2b-d0f8-4c1b-a5c8-fa57ce418379",
    nicon_category_name: "Truck",
    cei_policy_type: "Commercial",
  },
  /** MotorCycle – no NICON product exists on the backend */
  MotorCycle: {
    id: null,
    product_name: "MotorCycle - Third Party Insurance",
    product_price: 5000,
    product_category: "MotorCycle",
    pin_type: "MotorCycle",
    nicon_category_id: null,
    nicon_category_name: null,
    cei_policy_type: null,
  },
  /** TriCycle – no NICON product exists on the backend */
  TriCycle: {
    id: null,
    product_name: "TriCycle - Third Party Insurance",
    product_price: 3000,
    product_category: "TriCycle",
    pin_type: "TriCycle",
    nicon_category_id: null,
    nicon_category_name: null,
    cei_policy_type: null,
  },
};

export const CEI_MAKES = [
  { id: "48", name: "Toyota" },
  { id: "18", name: "Honda" },
  { id: "20", name: "Hyundai" },
  { id: "25", name: "Kia" },
  { id: "30", name: "Mercedes-Benz" },
  { id: "15", name: "Ford" },
  { id: "37", name: "Peugeot" },
  { id: "486", name: "KINGO" },
  { id: "298", name: "Ac Cobra" },
  { id: "1", name: "Acura" },
  { id: "126", name: "Admiral" },
  { id: "276", name: "Agrale" },
  { id: "306", name: "Akira" },
  { id: "322", name: "Aktra" },
  { id: "2", name: "Alfa" },
  { id: "255", name: "Alsvin" },
  { id: "394", name: "Amaecom" },
  { id: "3", name: "AMC" },
  { id: "380", name: "AMPRI" },
  { id: "189", name: "Apache" },
  { id: "361", name: "Aprilia" },
  { id: "383", name: "Arm" },
  { id: "430", name: "Armoured" },
  { id: "270", name: "Aryan" },
  { id: "64", name: "Ashok Leyland" },
  { id: "261", name: "Asia" },
  { id: "209", name: "Aston" },
  { id: "391", name: "Aston Martin" },
  { id: "224", name: "Astra" },
  { id: "293", name: "Atul" },
  { id: "4", name: "Audi" },
  { id: "195", name: "Austin" },
  { id: "311", name: "Autodex" },
  { id: "302", name: "Avant" },
  { id: "456", name: "AVATAR" },
  { id: "344", name: "B-2" },
  { id: "299", name: "BAIC" },
  { id: "55", name: "Bajaj" },
  { id: "446", name: "Baotian" },
  { id: "163", name: "Basuki" },
  { id: "115", name: "Baw" },
  { id: "373", name: "BAWC" },
  { id: "448", name: "Bazuki" },
  { id: "112", name: "Bedford" },
  { id: "450", name: "Belarus" },
  { id: "152", name: "Bentley" },
  { id: "329", name: "Berliet" },
  { id: "330", name: "Berliet Truck" },
  { id: "5", name: "Bertone" },
  { id: "437", name: "BikeMaster" },
  { id: "321", name: "Biyong" },
  { id: "234", name: "Bluebird" },
  { id: "6", name: "BMW" },
  { id: "249", name: "Bocat" },
  { id: "335", name: "BOMAG" },
  { id: "400", name: "Bradley" },
  { id: "388", name: "Braun" },
  { id: "78", name: "Brilliance" },
  { id: "7", name: "Buick" },
  { id: "178", name: "BYD" },
  { id: "8", name: "Cadillac" },
  { id: "291", name: "Can am spyder" },
  { id: "305", name: "Capital" },
  { id: "350", name: "Cardon" },
  { id: "113", name: "Cargo" },
  { id: "183", name: "Carter" },
  { id: "135", name: "Case" },
  { id: "123", name: "CAT" },
  { id: "395", name: "CCIC" },
  { id: "256", name: "Chagfeng" },
  { id: "465", name: "Champion" },
  { id: "399", name: "Champow" },
  { id: "104", name: "Chana" },
  { id: "70", name: "Changan" },
  { id: "262", name: "Changhe" },
  { id: "396", name: "Changhe Freedom" },
  { id: "462", name: "Changzhou" },
  { id: "423", name: "Chanlin" },
  { id: "381", name: "ChanLine" },
  { id: "427", name: "Cherokee" },
  { id: "124", name: "Cherry" },
  { id: "9", name: "Chevrolet" },
  { id: "403", name: "chongqing" },
  { id: "10", name: "Chrysler" },
  { id: "417", name: "CHTC" },
  { id: "425", name: "Chuangxin" },
  { id: "196", name: "Chunzhou" },
  { id: "245", name: "Cimc" },
  { id: "271", name: "Cisterna" },
  { id: "101", name: "Citroen" },
  { id: "352", name: "CLAAS" },
  { id: "441", name: "Classic" },
  { id: "375", name: "CMC" },
  { id: "192", name: "Commander" },
  { id: "408", name: "Compact" },
  { id: "451", name: "Covette" },
  { id: "143", name: "Crane" },
  { id: "439", name: "Cummins" },
  { id: "405", name: "Cushman" },
  { id: "162", name: "Dacia" },
  { id: "239", name: "Dadi" },
  { id: "11", name: "Daewoo" },
  { id: "66", name: "DAF" },
  { id: "12", name: "Daihatsu" },
  { id: "445", name: "Dasuki" },
  { id: "119", name: "Datsun" },
  { id: "297", name: "Dayang" },
  { id: "91", name: "Daylong" },
  { id: "193", name: "Dayun" },
  { id: "339", name: "DE-DAMAK" },
  { id: "281", name: "Degab" },
  { id: "346", name: "Dennis" },
  { id: "402", name: "Denstar" },
  { id: "333", name: "Derbi" },
  { id: "367", name: "DETOMASO" },
  { id: "137", name: "Dfac" },
  { id: "447", name: "DFM" },
  { id: "227", name: "DFSK" },
  { id: "364", name: "Diamond Reo" },
  { id: "382", name: "Dieci" },
  { id: "13", name: "Dodge" },
  { id: "208", name: "Dongfeng" },
  { id: "275", name: "Doosan" },
  { id: "231", name: "Doosan Infracore" },
  { id: "358", name: "DR" },
  { id: "296", name: "Dragon" },
  { id: "466", name: "DS" },
  { id: "223", name: "Ducati" },
  { id: "371", name: "DULEVO" },
  { id: "248", name: "Dumec" },
  { id: "228", name: "Dynapac" },
  { id: "14", name: "Eagle" },
  { id: "469", name: "EBIKE" },
  { id: "167", name: "Eicher" },
  { id: "369", name: "EMI" },
  { id: "180", name: "ERF" },
  { id: "431", name: "ERRAUD" },
  { id: "349", name: "Eval" },
  { id: "420", name: "Fabtol" },
  { id: "286", name: "Famtrac" },
  { id: "418", name: "Farmax" },
  { id: "230", name: "Faun" },
  { id: "157", name: "FAW" },
  { id: "202", name: "Fenco" },
  { id: "459", name: "FENGXING" },
  { id: "164", name: "Ferari" },
  { id: "169", name: "Ferguson Massey" },
  { id: "73", name: "FIAT" },
  { id: "331", name: "Figo" },
  { id: "288", name: "Fiori" },
  { id: "251", name: "Fire" },
  { id: "292", name: "Foday" },
  { id: "203", name: "Foden" },
  { id: "171", name: "Force" },
  { id: "120", name: "Forland" },
  { id: "85", name: "Foton" },
  { id: "61", name: "Frajend" },
  { id: "125", name: "Freightliner" },
  { id: "379", name: "FUJIA" },
  { id: "82", name: "GAC" },
  { id: "343", name: "Galaxy" },
  { id: "436", name: "Gallant" },
  { id: "277", name: "Gazelle" },
  { id: "74", name: "Geely" },
  { id: "236", name: "Genie" },
  { id: "428", name: "Genlyon" },
  { id: "16", name: "Geo" },
  { id: "17", name: "GMC" },
  { id: "424", name: "GME" },
  { id: "323", name: "Go Well" },
  { id: "110", name: "GOLDEN DRAGON" },
  { id: "133", name: "Gonow" },
  { id: "307", name: "GP Mobile" },
  { id: "63", name: "Grand" },
  { id: "99", name: "Great Wall" },
  { id: "138", name: "Grove" },
  { id: "410", name: "GUZZI" },
  { id: "304", name: "Hafei" },
  { id: "93", name: "Haima" },
  { id: "407", name: "Hako" },
  { id: "312", name: "Haojin" },
  { id: "100", name: "Haojue" },
  { id: "301", name: "Harley" },
  { id: "218", name: "HAWTAI" },
  { id: "452", name: "Hero" },
  { id: "134", name: "Hiab" },
  { id: "155", name: "Higer" },
  { id: "433", name: "HIJOY" },
  { id: "197", name: "Hino" },
  { id: "184", name: "Hitachi" },
  { id: "263", name: "Holden" },
  { id: "253", name: "Holland Fatagri" },
  { id: "497", name: "Honda-Motocycle" },
  { id: "205", name: "Hongyang" },
  { id: "490", name: "Hongzhou" },
  { id: "98", name: "Hover" },
  { id: "105", name: "Howo" },
  { id: "260", name: "Huanghai" },
  { id: "19", name: "Hummer" },
  { id: "458", name: "Hunter" },
  { id: "229", name: "Hyosung" },
  { id: "269", name: "Hyster" },
  { id: "168", name: "Hyva" },
  { id: "226", name: "IHI" },
  { id: "109", name: "Indian" },
  { id: "21", name: "Infiniti" },
  { id: "432", name: "Ingersoll-Rand" },
  { id: "81", name: "Innoson" },
  { id: "142", name: "International" },
  { id: "273", name: "Irizar" },
  { id: "22", name: "Isuzu" },
  { id: "56", name: "Iveco" },
  { id: "357", name: "IX" },
  { id: "108", name: "JAC" },
  { id: "23", name: "Jaguar" },
  { id: "274", name: "Jcb" },
  { id: "24", name: "Jeep" },
  { id: "435", name: "Jet" },
  { id: "460", name: "Jetour" },
  { id: "200", name: "Jianshe" },
  { id: "84", name: "Jinbei" },
  { id: "118", name: "Jincheng" },
  { id: "387", name: "Jiuxing" },
  { id: "172", name: "JMC" },
  { id: "282", name: "John" },
  { id: "287", name: "Jonway" },
  { id: "146", name: "Joylong" },
  { id: "491", name: "Juncheng" },
  { id: "463", name: "KAISA" },
  { id: "207", name: "Kalmar" },
  { id: "94", name: "Kama" },
  { id: "426", name: "KARY-GO" },
  { id: "313", name: "Kasan" },
  { id: "90", name: "Kasea" },
  { id: "365", name: "Kavaki" },
  { id: "95", name: "Kawasaki" },
  { id: "284", name: "Kayak" },
  { id: "201", name: "Kc Sanya" },
  { id: "372", name: "Keeway" },
  { id: "340", name: "KEIHIN MDGS" },
  { id: "181", name: "Keke" },
  { id: "165", name: "Kenworth" },
  { id: "156", name: "Keystone" },
  { id: "80", name: "King" },
  { id: "443", name: "King Long" },
  { id: "210", name: "Kingkan" },
  { id: "413", name: "kinglong" },
  { id: "79", name: "Kingston" },
  { id: "235", name: "Kobelco" },
  { id: "300", name: "Korando" },
  { id: "222", name: "KTM" },
  { id: "140", name: "KYMCO" },
  { id: "370", name: "Kymstone" },
  { id: "406", name: "LADA" },
  { id: "495", name: "LAMBERT" },
  { id: "266", name: "Lamborghini" },
  { id: "161", name: "Lancia" },
  { id: "26", name: "Land Rover" },
  { id: "216", name: "LANDINI" },
  { id: "214", name: "LANDINI GLOBALFARM" },
  { id: "215", name: "LANDINI LP" },
  { id: "198", name: "Landwind" },
  { id: "213", name: "LAN-P" },
  { id: "187", name: "LDV" },
  { id: "27", name: "Lexus" },
  { id: "97", name: "Leyland" },
  { id: "71", name: "Liberty" },
  { id: "412", name: "LIEBAO" },
  { id: "186", name: "Liebherr" },
  { id: "106", name: "Lifan" },
  { id: "28", name: "Lincoln" },
  { id: "257", name: "Linde R20 Forklift" },
  { id: "250", name: "LINDE R20 S FORKLIFT" },
  { id: "414", name: "Lingkin" },
  { id: "457", name: "Little" },
  { id: "324", name: "Loncin" },
  { id: "419", name: "LTI" },
  { id: "258", name: "M/BENZ CLS 500" },
  { id: "57", name: "MAC" },
  { id: "72", name: "MACK" },
  { id: "442", name: "MAG INTERNATIONAL" },
  { id: "318", name: "Magirus" },
  { id: "268", name: "MAHINDRA" },
  { id: "103", name: "Make" },
  { id: "332", name: "Malossi" },
  { id: "65", name: "MAN" },
  { id: "206", name: "Manitou" },
  { id: "355", name: "Marcopolo S.A" },
  { id: "422", name: "Mars" },
  { id: "221", name: "Maruti" },
  { id: "182", name: "Maserati" },
  { id: "409", name: "Matrix 100" },
  { id: "160", name: "Maxus" },
  { id: "316", name: "MayBach" },
  { id: "29", name: "Mazda" },
  { id: "389", name: "McLaren" },
  { id: "351", name: "MCV" },
  { id: "319", name: "MDGS" },
  { id: "149", name: "MEIYA" },
  { id: "31", name: "Mercury" },
  { id: "32", name: "Merkur" },
  { id: "283", name: "Merlo" },
  { id: "128", name: "MG" },
  { id: "289", name: "MHRX" },
  { id: "374", name: "Midas" },
  { id: "468", name: "MIKANO" },
  { id: "33", name: "MINI" },
  { id: "34", name: "Mitsubishi" },
  { id: "129", name: "MOBITI" },
  { id: "303", name: "Moto" },
  { id: "144", name: "Motobi" },
  { id: "267", name: "MOTORCYCLE" },
  { id: "492", name: "Motorcycle" },
  { id: "310", name: "Mudan" },
  { id: "454", name: "Muoner" },
  { id: "334", name: "MV" },
  { id: "148", name: "NANFANG" },
  { id: "141", name: "NASARA" },
  { id: "150", name: "Navister International" },
  { id: "254", name: "Neoplan" },
  { id: "482", name: "NEW FIRE" },
  { id: "280", name: "Nippon" },
  { id: "35", name: "Nissan" },
  { id: "453", name: "Nord" },
  { id: "294", name: "Norge" },
  { id: "122", name: "NTM" },
  { id: "86", name: "Octavia" },
  { id: "92", name: "OGBUAWA" },
  { id: "36", name: "Oldsmobile" },
  { id: "53", name: "Opel" },
  { id: "220", name: "Pegaso" },
  { id: "377", name: "PERFORMER" },
  { id: "151", name: "Peterbilt" },
  { id: "59", name: "Piaggio" },
  { id: "368", name: "PICCASO" },
  { id: "38", name: "Pininfarina" },
  { id: "309", name: "Pioneer" },
  { id: "498", name: "Plagio" },
  { id: "39", name: "Plymouth" },
  { id: "401", name: "Polaris" },
  { id: "179", name: "Polarsun" },
  { id: "40", name: "Pontiac" },
  { id: "41", name: "Porsche" },
  { id: "237", name: "Powerplus" },
  { id: "416", name: "Preet" },
  { id: "429", name: "Premier" },
  { id: "145", name: "President" },
  { id: "362", name: "Prezda" },
  { id: "185", name: "Proton" },
  { id: "211", name: "Pulsar" },
  { id: "130", name: "QINGQI" },
  { id: "68", name: "Qlink" },
  { id: "188", name: "Quad" },
  { id: "464", name: "RAMBLER AMBASSADOR" },
  { id: "67", name: "Range" },
  { id: "42", name: "Renault" },
  { id: "194", name: "Rexton" },
  { id: "353", name: "Rhino" },
  { id: "404", name: "Rico" },
  { id: "444", name: "RKV" },
  { id: "191", name: "Robinson" },
  { id: "153", name: "Rocky" },
  { id: "75", name: "ROLLS-ROYCE" },
  { id: "455", name: "RONGXING" },
  { id: "166", name: "Rosenbauer" },
  { id: "62", name: "Rover" },
  { id: "114", name: "Royal" },
  { id: "484", name: "RUMION" },
  { id: "43", name: "Saab" },
  { id: "398", name: "SAIC" },
  { id: "347", name: "Saipa" },
  { id: "173", name: "Samsung" },
  { id: "212", name: "Sany" },
  { id: "199", name: "Sanyang" },
  { id: "44", name: "Saturn" },
  { id: "87", name: "Savana" },
  { id: "77", name: "Scania" },
  { id: "127", name: "Scion" },
  { id: "58", name: "SEAT" },
  { id: "102", name: "Seddon" },
  { id: "488", name: "SEM" },
  { id: "345", name: "Sento" },
  { id: "233", name: "Shacman" },
  { id: "190", name: "Shanghai" },
  { id: "219", name: "Shelby" },
  { id: "242", name: "SHIFENG" },
  { id: "158", name: "Shineray" },
  { id: "440", name: "Shiroro" },
  { id: "246", name: "Shovel" },
  { id: "232", name: "Shuanghuan" },
  { id: "217", name: "Shuang-huan" },
  { id: "359", name: "Silfox" },
  { id: "88", name: "Simba" },
  { id: "117", name: "Sino" },
  { id: "60", name: "sinoki" },
  { id: "52", name: "Skoda" },
  { id: "121", name: "Skygo" },
  { id: "176", name: "SMA" },
  { id: "243", name: "Smart" },
  { id: "461", name: "Sonlink" },
  { id: "496", name: "SONLINK" },
  { id: "393", name: "Spartan" },
  { id: "363", name: "SsangYong" },
  { id: "116", name: "Ssanyoung" },
  { id: "177", name: "Stallion" },
  { id: "45", name: "Sterling" },
  { id: "107", name: "STEYR" },
  { id: "46", name: "Subaru" },
  { id: "147", name: "Sukida" },
  { id: "139", name: "Super" },
  { id: "47", name: "Suzuki" },
  { id: "360", name: "Swaraj" },
  { id: "376", name: "SYM" },
  { id: "354", name: "TAK PERFORMER" },
  { id: "493", name: "Target" },
  { id: "320", name: "Tarsung" },
  { id: "83", name: "Tata" },
  { id: "252", name: "Taylor" },
  { id: "342", name: "TEC" },
  { id: "265", name: "Terberg" },
  { id: "136", name: "Terex" },
  { id: "131", name: "Terrain" },
  { id: "397", name: "Tesla" },
  { id: "467", name: "test" },
  { id: "411", name: "TEZRAFTAAR" },
  { id: "384", name: "Thomas" },
  { id: "449", name: "Tiffin" },
  { id: "238", name: "T-King" },
  { id: "485", name: "T-KING" },
  { id: "378", name: "Tongjiafu" },
  { id: "314", name: "TopStar" },
  { id: "154", name: "Torino" },
  { id: "241", name: "Toyota hiace pick/up" },
  { id: "315", name: "Tractor" },
  { id: "278", name: "Triumph" },
  { id: "438", name: "Trumpchi" },
  { id: "317", name: "Tuktuk" },
  { id: "76", name: "TVS" },
  { id: "341", name: "TVT" },
  { id: "290", name: "Twinco" },
  { id: "264", name: "UAZ" },
  { id: "338", name: "Ursus" },
  { id: "415", name: "UVA" },
  { id: "240", name: "vanhool" },
  { id: "132", name: "Vauxhall" },
  { id: "390", name: "VDL Bova" },
  { id: "325", name: "Vespa" },
  { id: "386", name: "Victory" },
  { id: "225", name: "Vietz" },
  { id: "327", name: "Vogue" },
  { id: "295", name: "Volat" },
  { id: "49", name: "Volkswagen" },
  { id: "50", name: "Volvo" },
  { id: "366", name: "Wanhoo" },
  { id: "326", name: "Western Star" },
  { id: "336", name: "Wheel" },
  { id: "489", name: "WHEEL LOADER" },
  { id: "392", name: "White Knuckle" },
  { id: "385", name: "Wodeli" },
  { id: "175", name: "Wrangler" },
  { id: "170", name: "Wuling" },
  { id: "285", name: "Wuyang" },
  { id: "483", name: "XCMG " },
  { id: "328", name: "XFMCO" },
  { id: "244", name: "xinkai" },
  { id: "434", name: "Yakeda" },
  { id: "356", name: "Yale" },
  { id: "89", name: "Yamaha" },
  { id: "308", name: "Yamasaki" },
  { id: "174", name: "Yaxing" },
  { id: "421", name: "Yochi" },
  { id: "259", name: "Yorobo Apsonic" },
  { id: "96", name: "YUEJIN" },
  { id: "51", name: "Yugo" },
  { id: "159", name: "Yutong" },
  { id: "204", name: "Zahav" },
  { id: "337", name: "zarsung" },
  { id: "494", name: "Zhejiang" },
  { id: "247", name: "ZHONGTONG" },
  { id: "54", name: "Zhongxing" },
  { id: "348", name: "ZNA-RICH" },
  { id: "279", name: "Zonsheng" },
  { id: "272", name: "Zoomlion" },
  { id: "69", name: "Zotye" },
  { id: "487", name: "ZOYTE" },
  { id: "111", name: "Zx Auto" },
];

export const NICON_MAKES = [
  // Prioritized Brands
  { id: "98a7583e-a40f-4977-a2dc-451bf1941ffd", name: "Toyota" },
  { id: "98a7583f-3a86-499d-a875-c29d6604ab81", name: "Honda" },
  { id: "98a7583f-e5ae-484f-b287-81c2a6e91715", name: "Hyundai" },
  { id: "98a7583e-b0d2-4d6b-b304-d72388aec54d", name: "Kia" },
  { id: "98a7583e-b6f8-4f04-a500-d7a5dd1b7625", name: "Mercedes-Benz" },
  { id: "98a7583e-ba09-48b8-aba3-a8d11deca4c1", name: "BMW" },
  { id: "98a7583e-bfa9-4da0-8dac-c4ea005f2f75", name: "Ford" },
  { id: "98a75840-a181-4496-927d-22ef8f41fd20", name: "Peugeot" },
  { id: "a02d0904-e42a-42d2-9b50-4263f2d9cfb9", name: "Lexus" },
  { id: "98a7583e-c3e5-4fd7-a6f6-b1450faefd9b", name: "Volkswagen" },
  { id: "98a7583f-75f0-4ae2-99c5-c523adba9445", name: "Volvo" },
  { id: "99668207-3e41-42e3-9618-aa88aa400932", name: "NISSAN" },
  { id: "99906ef6-59e2-466b-b3f2-9e292192a5b9", name: "MAZDA" },
    { id: "98a7583f-2315-4c9c-a0a9-81fb4b5a111a", name: "Suzuki" },
  { id: "99666224-43c0-458a-ae10-ea604dca97ef", name: "AC" },
  { id: "99666e78-9440-49de-8763-99ed69507792", name: "AC" },
  { id: "99667a02-a5d0-435a-8507-cc58b5f3e177", name: "AC" },
  { id: "9bf2336f-063e-4ee7-a222-97306f864b03", name: "AC HIJET" },
  { id: "9ae8cb4f-d9dc-427c-b431-c8cb7bfdca54", name: "AC SCOOTY SYM" },
  { id: "9b71cb4a-af3a-4a9f-b2ed-cc4b42bb8a23", name: "AC TD" },
  { id: "9982da33-f559-48a2-9c69-7b2450d98de9", name: "ACURA" },
  { id: "9988becc-641d-4620-ad38-2748a6b70a49", name: "ACURA" },
  { id: "9a47cd37-d726-48d4-abfa-b6d43bed9858", name: "ACURA" },
  { id: "9a6a25aa-ee58-4b0a-b244-d00745f28168", name: "ACURA RDX" },
  { id: "98a75845-bdaf-4644-a0d9-7005e3c98186", name: "AMC" },
  { id: "9b00d700-5672-4d7b-9ff5-708c56884c49", name: "ASHOK LEYLAND" },
  { id: "9bd29783-c4fc-444c-afe4-6259b6a292c8", name: "ASHOK LEYLAND" },
  { id: "99b0d50b-1417-4c28-a860-82650bba7368", name: "ASTON" },
  { id: "99d75e5a-2289-4c4e-bbe2-23d5cc8e006c", name: "ASTRA" },
  { id: "9b276b79-7749-4b64-8f8c-b49ea21f214c", name: "ASTRAL" },
  { id: "993049c2-f5dc-4d16-b4b1-e69a0370a79a", name: "AVATA MOTORCYCLE" },
  { id: "98a75840-9389-46d3-b7ae-af4fbf426adb", name: "Acura" },
  { id: "98a75844-3a30-41ad-a7a1-b8c80265c05d", name: "Alfa Romeo" },
  { id: "9ae0909b-5827-4fc9-a6e6-34678014923b", name: "Argentina" },
  { id: "9b372794-b1b0-4895-b992-1044331fdbe4", name: "Atrai" },
  { id: "9b372cb9-0873-4d3a-9274-83cbdbea9054", name: "Atrai" },
  { id: "98a7583e-e3e2-4080-bc87-44660af77de2", name: "Audi" },
  { id: "994ac0eb-47aa-4ee8-97ec-46a5c487aa84", name: "Audi" },
  { id: "996bcdab-6006-4fb3-ab34-06728f99c198", name: "Audi 80" },
  { id: "9bfcd561-ca9d-4303-bddc-75b8b4f1fe94", name: "Aura" },
  { id: "9a83587f-798f-497c-9ce9-8cd29d571aa1", name: "BAJAJ" },
  { id: "9988c89d-31ce-4c32-bce5-a128018a7269", name: "BAOTIAN BT" },
  { id: "9b2d1e9a-1b6b-41f6-83d1-3cd0337f02e4", name: "BAW" },
  { id: "9a014772-bba4-46c8-83e4-2c176f0f4dd1", name: "BEDFORD" },
  { id: "9b19441f-922f-4e94-bba4-9eb4e367b03d", name: "BEIFAN" },
  { id: "9a9e1004-8c6c-4862-8343-fa6ba92c0302", name: "BENTLEY" },
  { id: "98a7583e-c9b6-4048-b95b-56d7dfec8817", name: "Bajaj" },
  { id: "994a555c-0986-41d2-a3a9-ed74ecb962a4", name: "Bajaj" },
  { id: "9996b6df-83ba-4556-8253-df78926af0c9", name: "Bajaj tricycle" },
  { id: "9ba7f9f3-5e24-4814-b6d7-7136e3c8155d", name: "Beijing" },
  { id: "98a75843-21c4-4430-97d2-b872f0bd92e0", name: "Bertone" },
  { id: "98a7583f-06c4-4134-828c-418e494ea18a", name: "Brilliance" },
  { id: "98a7583f-8bc3-4a4a-bb7c-eb7ad41ae71f", name: "Buick" },
  { id: "99c14949-4e23-4bf3-a072-009142cc189f", name: "CARTER TEC 125" },
  { id: "99125deb-22df-4cd5-82e7-2718baac9f5b", name: "CARTER-TEC" },
  { id: "9b2f5e7a-3d17-4a37-8e1b-b8b390aafb93", name: "CATERPILLAR" },
  { id: "992e7583-2baf-4426-bf5d-358d64d5ca6e", name: "CHANA" },
  { id: "9b6fdf9a-5b95-4466-b43b-f201a51e4225", name: "CHEROKEE" },
  { id: "9bd29133-62b7-4159-b653-97151426baab", name: "CHERRY" },
  { id: "98f9f628-af23-4232-93f8-72c47366a5c5", name: "CHEVROLET" },
  { id: "9b00c56d-f3a1-448a-8f0f-1c3bdaeff0c1", name: "CHRYSLER" },
  { id: "9bd1d6ef-8f9b-4cd3-9ac3-01578914672d", name: "CITI MOTORCYCLE" },
  { id: "99950b24-1802-453b-9865-7cf5d27ab6ba", name: "CITROEN" },
  { id: "9a29cd35-ca11-433c-ac10-24785eeb5a5b", name: "CITROEN" },
  { id: "9ae0dcdd-49f8-4bd3-9265-35901c197243", name: "COMMANDER" },
  { id: "98a75844-c5bb-49b6-ac40-faf1a382e13d", name: "Cadillac" },
  { id: "98fa5664-af8d-425d-a957-597194d8d99d", name: "Car-Go" },
  { id: "99a4e76d-bcc6-4908-b62c-167d8d5f7065", name: "Car-Go 60" },
  {
    id: "9996b774-f50b-43cc-a65c-55823fa14c7f",
    name: "Carter Tee ladies motorcycle",
  },
  { id: "9a8e77c9-2293-4916-a449-c6a7cf5b476e", name: "Changan" },
  { id: "9bba2a7b-91b6-49d2-9e61-14dc7c99d2d0", name: "Changan" },
  { id: "9bf44f6c-2a7f-47bf-bb73-2849d62562fb", name: "Changan" },
  { id: "9c289b6f-f882-4a6a-b6db-0b2920d56446", name: "Changan" },
  { id: "98a7583f-1e77-4873-a0fd-3b78741c1522", name: "Chevrolet" },
  { id: "991e1a01-4b5b-4875-a699-283bb431e118", name: "Chevrolet" },
  { id: "99a53fb8-1d64-494f-b8e9-50cfad60f392", name: "Chevrolet" },
  { id: "9d92ea5e-9be4-4ae3-a29e-0b75457f7004", name: "Chevrolet" },
  { id: "98a7583e-d937-40c0-8242-d0640c9e74bb", name: "Chrysler" },
  { id: "9a1da80e-a04e-45f8-98a0-42c3c116ea79", name: "Citron" },
  { id: "99f3497a-1869-418b-9702-87ee0e582ee2", name: "Compass" },
  { id: "9baf91a2-e16c-4dcc-8992-5e6a2dc9b27d", name: "DAEWOO" },
  { id: "98a75843-c38c-4ef4-904b-7237a105fdf6", name: "DAF" },
  { id: "9a7fa1de-eda5-44f4-a94b-5d80313143d1", name: "DAIHATSU" },
  { id: "995648b1-b668-4996-afe7-78d943e41a7c", name: "DATSUN" },
  { id: "9c0c7697-f861-4ed9-b54b-6c9b8dd68454", name: "DAYIL" },
  { id: "9a796b4b-025b-4fcd-a155-a051396190f9", name: "DAYUN" },
  { id: "9a58623e-e283-4700-a652-528391cdc826", name: "DELICA" },
  { id: "994c978f-8179-4dc1-9de3-99af0ac8a4a3", name: "DENSTAR" },
  { id: "9b538be2-2225-43c0-98e8-ed86a7a7914a", name: "DFAC" },
  { id: "9b538c03-4541-42b1-82fd-2905870d2233", name: "DFAC" },
  { id: "9a47c69f-214f-4f2a-9e3b-04ac792cd372", name: "DODGE" },
  { id: "9a828a44-ed0b-448f-b75a-720c681bbdc4", name: "DUMP" },
  { id: "9c0e997a-491a-4173-8d2e-dfe6bcbc48fa", name: "DUMPER" },
  { id: "9a056d88-7674-4bab-918b-8bbd1e6ab5f2", name: "Dacia" },
  { id: "9abce005-a771-4ca9-836f-0a89b81ad09c", name: "Dacia" },
  { id: "98a75840-d8e1-46ee-a63a-b9a0c13b6cae", name: "Daewoo" },
  { id: "98a75840-53ba-4f81-9d91-ef75e99b3a1f", name: "Daihatsu" },
  { id: "98a7583f-4754-4029-87fe-4a30fe65e214", name: "Daihatu" },
  { id: "98a75840-b392-4f7a-af74-af82104f0484", name: "Daylong" },
  { id: "9934a382-96fc-4f88-85cb-43bdbfdd8553", name: "Daylong" },
  { id: "9934a385-86d4-45ab-a66c-18e2a5607319", name: "Daylong" },
  { id: "99c0f66c-ffb8-4ac7-96cf-cba0c9ace9e7", name: "Daylong motorcycle" },
  { id: "98a75841-c026-4cce-b082-c180003bc0b9", name: "Denstar Motorcycle" },
  { id: "98a7583f-cbd9-4a80-b1d4-cf304d036622", name: "Dodge" },
  { id: "99e57415-a93f-4acd-9c59-4c2c97f53d08", name: "Dodge" },
  { id: "99503a48-4e25-46f1-a991-7615c394fb51", name: "Dong Feng" },
  { id: "98a75845-e367-400b-a01d-800b5ef35778", name: "Dragon" },
  { id: "9b8193fb-c7e9-4397-a775-47c9f58f62e2", name: "ECONO" },
  { id: "998ec293-6c68-4bb9-9d93-8ad9619b8563", name: "EMGRAND SALOON" },
  { id: "99227061-8898-4f0f-a57c-e8a6ef5d1f39", name: "ENCLAVE" },
  { id: "996c4d53-67d1-46a7-9678-eb15e57a8bda", name: "ERISCO" },
  { id: "993e6084-d5a8-4d2b-a82b-6ab135df48b1", name: "ERRAND" },
  { id: "99c1492a-11ac-4fc4-94bb-eba9bafca9bc", name: "ERRAND 150" },
  { id: "98a7583f-dad2-4e8b-8987-36e3921d26de", name: "Eagle" },
  { id: "99142385-47d1-44b9-9fa5-3e88cee3b58f", name: "Errand 150" },
  { id: "98a7583e-b3cb-4a55-95b0-4036110dcf29", name: "FAW" },
  { id: "9b75c57f-338c-4070-9e20-ab09273ec900", name: "FAW" },
  { id: "9a0f97d3-fa21-49d2-88f8-345070958d66", name: "FENCO MOTORCYCLE" },
  { id: "99ab5132-a4a6-4f59-ac77-c15fafd2fb2a", name: "FIAT IVECO" },
  { id: "98a75841-2e21-4961-ac75-26b680adb4a2", name: "FORLAND" },
  { id: "98f9c097-dbb3-4ce0-9aa4-d4b359115f1c", name: "FORTUNE" },
  { id: "98a75844-88ac-4f3d-9f37-c87a80894049", name: "FOTON" },
  { id: "99221605-b703-43e3-a969-c46f59a8f52b", name: "FOTON VIEW" },
  { id: "98a75843-76d9-4eec-9c38-386dc047420d", name: "Fiat" },
  { id: "991244ec-8f26-4943-acab-095f96cdfdcf", name: "Freight" },
  { id: "98a75841-705c-4b54-a71b-197110c3e0cc", name: "GAC" },
  { id: "99bd0955-3994-4a96-81be-5de349861bb3", name: "GALLANT MOTORCYCLE" },
  { id: "9d848ecc-9fcf-4919-9385-95a69eaa10d4", name: "GEELY" },
  { id: "994e3ef9-acaa-40d7-807f-da731c1335d5", name: "GLINK" },
  { id: "98a7583f-097a-4872-85bd-b0cad6f55490", name: "GMC" },
  { id: "9b6daae1-59d0-43e1-94de-e29f9e350076", name: "GOLDEN" },
  { id: "99ab18bf-1604-484a-b1c8-30515ca20510", name: "GRAND" },
  { id: "9be005db-9088-41c2-b25a-8e13a2da4de5", name: "GREAT WALL" },
  { id: "98a7583f-807e-4a45-998a-cde931519b73", name: "GROVE" },
  { id: "9b7d85ed-b7c0-42d7-b865-d5f7f9b05561", name: "Geely" },
  { id: "98a75843-46ec-4a8d-b279-3adbe9029d72", name: "Gelly" },
  { id: "99125e3e-b430-4d37-9d58-956509d756a1", name: "HAOJIN" },
  { id: "99669f6b-fb25-4222-90a6-690aaa9f7e89", name: "HAOJIN" },
  { id: "98a75846-06f5-450e-be81-2621b81b4f36", name: "HAOJUE" },
  { id: "99568067-dd11-49bc-83f0-9eebe2fb7757", name: "HAOJUE" },
  { id: "98a75844-1afa-4241-b218-29b590cc4c1a", name: "HI" },
  { id: "99247f65-d45f-4841-8053-155880bd88bb", name: "HIJET" },
  { id: "99795970-e7f4-46dc-b2b6-247f0ed21910", name: "HIJET" },
  { id: "9a942458-9955-4caf-b5fc-0bf857bbd037", name: "HINO" },
  { id: "99cd4a71-40ff-4c6c-8a18-fd06b3b01955", name: "HONDA MOTORCYCLE" },
  { id: "99f32549-344a-4bda-8f2c-d065c9a157a6", name: "HONDA MOTORCYCLE" },
  { id: "99e90c46-d28c-45e9-9c79-126f808073df", name: "HONDA TODAY" },
  { id: "9ae6c361-978b-4663-9b63-29e819858a1f", name: "HOWO" },
  { id: "9978cc89-2072-4e33-becd-ddc8b0e1bbfb", name: "HUNTER" },
  { id: "99a4989a-7dca-48a9-aabf-ccf652612cbe", name: "HUSKY 150" },
  { id: "98a75846-11a0-4873-b94c-2cdcb6fee614", name: "Haima Car" },
  { id: "9b5fa526-e3f1-4a3a-b6e6-29194e8c3729", name: "Hover" },
  { id: "9b5fa982-3c93-4586-b2cf-60be5e312689", name: "Hover" },
  { id: "98a75844-57c8-40e4-8aaa-2ecb9f7a8331", name: "Howo Truck" },
  { id: "98a75843-3ab4-42ba-be52-62d678bd325a", name: "Hummer" },
  { id: "9a8ebb35-0550-4706-9f02-6a28badd221d", name: "INNOSON" },
  { id: "98a75840-7577-4915-a25c-fab9613b675f", name: "IVECO" },
  { id: "99ab507a-ff3e-47f5-88e4-ac6db3d8ffc5", name: "IVECO" },
  { id: "9d928120-414c-455f-9490-2010e55d4180", name: "IVECO" },
  { id: "9a13cab4-e885-48a6-883d-c0f96610d945", name: "IVM" },
  { id: "98a7583e-cf5b-4ce4-83c8-d2fdb08910f5", name: "Infiniti" },
  { id: "9940865f-a5ba-4087-8816-edbbe9878657", name: "International" },
  { id: "98a7583f-6558-48ae-8689-e3f0ce35be31", name: "Isuzu" },
  { id: "98a75844-d931-4797-a3a3-f3527f5eff28", name: "JAC" },
  { id: "9a4a0171-65c2-4548-a686-17a4c5811f9a", name: "JAC" },
  { id: "9ae88f66-6350-479f-ac29-2b9704278d14", name: "JAGUAR" },
  { id: "9982c10e-4b55-4c08-9ad5-18feb885e92a", name: "JEEP" },
  { id: "9a6417f1-3ee2-4b02-9172-afea9c185ae2", name: "JEEP" },
  { id: "9c2caa0f-17c9-49d4-97b5-82d01572a389", name: "JEEP" },
  { id: "9b535e73-ef59-4997-ba12-44211f2f758d", name: "JET" },
  { id: "9d883eac-b4c9-4d57-beb9-eb34fd95a0b7", name: "JIANGSU" },
  { id: "9966c6d8-4836-4c0a-8c0d-60280c5cfeb8", name: "JINCHENG" },
  { id: "9d8ab8d9-7bc4-42b9-a5c5-e4962fd7b812", name: "JINCHENG" },
  { id: "991850ea-70b1-4d20-9090-3793cae3d1dd", name: "JMC" },
  { id: "9a55d1ef-bf1e-4a89-8a9e-1509753caad9", name: "JMC" },
  { id: "98a75841-56dd-4b20-a757-8e70458312ff", name: "JMC VIGUS" },
  { id: "98a75841-c89a-4ebc-8f8c-03fb47b2ed39", name: "Jaguar" },
  { id: "98a7583e-efdd-4e2a-8044-b4ad8579facf", name: "Jeep" },
  { id: "9a85fb1e-a617-474f-8811-796e05edb551", name: "Jinbei" },
  { id: "9b7dec54-4898-4855-9646-4bdc8346ffce", name: "KAMA" },
  { id: "9c10606f-ea18-441f-866d-ea1f620655ce", name: "KAMSO" },
  { id: "991638ec-daf9-40d6-ad39-73a02bec963e", name: "KARRY BUS" },
  { id: "99098f66-248e-40cf-9197-471dcf67dd47", name: "KAVAKI" },
  { id: "999d10eb-6c1d-42db-9d42-1a9b7ba3c233", name: "KEEWAY MOTORCYCLE" },
  { id: "9c2adadf-74cb-4ba5-a933-af2af8a91dad", name: "KENWORTH" },
  { id: "99689a3f-6e79-47e4-b127-383d961a4900", name: "KEYSTONE" },
  { id: "999100d5-06b7-4cdf-ac8c-54a62887b98c", name: "KINGSTON" },
  { id: "9b2d755a-9de8-418a-8633-6a4c227bd81b", name: "KYMCO" },
  { id: "99145918-01f8-4106-b5c6-5bd93ce964a9", name: "KYMSTONE 125" },
  { id: "9a0f979a-f726-4fe5-b4ab-07ac854f6517", name: "KYMSTONE MOTORCYCLE" },
  { id: "98a75842-fdf5-41a0-a525-4eb3fe4d8a38", name: "King" },
  { id: "9a1b740d-896a-4961-9d80-0aed78b3c53d", name: "Kingston" },
  { id: "99204c3b-c999-49d8-85cf-73d7e65e8ac0", name: "Kymco" },
  { id: "9ad6fbb9-07e6-47e4-ad5a-2f434430830c", name: "LCG" },
  { id: "9b3b372b-5752-4207-b5d8-9d55b0060e36", name: "LDV" },
  { id: "9d8d3338-c237-4ccc-bb15-e900dcefe499", name: "LIBERTY" },
  { id: "99401bba-2e23-4d13-8d74-bf947dac9e32", name: "LIFAN" },
  { id: "994aa5e9-0f19-4cf0-b173-c8d53d08413f", name: "LIFAN" },
  { id: "98a75840-eda8-41cc-afd9-ff26985cd8c8", name: "Land Rover" },
  { id: "9966a206-e5ba-4cc1-88c5-da2bf61f85ff", name: "Land Rover" },

  { id: "9909bcb0-66cd-4493-b613-ef05f455c229", name: "Leyland" },
  { id: "99567115-d12a-4f0d-9241-3cb2e5549178", name: "Liberty" },
  { id: "98a75842-4b02-4769-b174-f01bdc00163c", name: "Lifan" },
  { id: "98a75844-9306-40de-b3f6-ebe09b51a789", name: "Lincoln" },
  { id: "98a75843-8e00-480f-9ba1-b0902d98d376", name: "Lingken Motorcycle" },
  { id: "98a75842-71da-423f-922c-e333e6e2438b", name: "MACK" },
  { id: "98a75846-80e2-40d8-9708-719241c244f9", name: "MAN" },
  { id: "98a75841-b810-4b36-82d8-9ad14e060251", name: "MAN DIESEL" },
  { id: "9ab0d3fa-d2b9-4386-8fd5-6c7da94562f7", name: "MAN-VIN" },
  { id: "9a1d7ee9-8731-4aab-a7f5-258b398a2953", name: "MARS" },
  { id: "9a7e85ee-b15d-45ba-9dce-5c0cd84b0071", name: "MAX 8" },
  { id: "9b2b646a-f25e-4e7d-9b67-750bde1d9f22", name: "MAXDA" },

  { id: "9c0098c0-be1a-4453-92c8-316cf1587c98", name: "MEIYA" },
  { id: "99aaa240-1f55-4a95-be03-1f71ed029aae", name: "MERCEDES ACTROS" },
  { id: "98a75845-78ed-4f71-b0a1-5240f5159999", name: "Mercedes-Benz817" },
  { id: "98a75841-8fce-4223-9186-fea0e1f4dbb4", name: "Mercury" },
  { id: "9b8416cd-449e-4793-bcac-d317050ed3c2", name: "Mikano" },
  { id: "98a7583f-7a51-4645-9023-443e4a946219", name: "Mini Cooper" },
  { id: "98a7583e-ea87-46e3-aa28-083ae7476e32", name: "Mitsubishi" },
  { id: "994a9652-2f84-453d-af90-a6b4d7b995a4", name: "Mitsubishi" },
  { id: "9974d656-c010-416d-a9c5-5b1cb6c29da4", name: "Mitsubishi" },
  { id: "9974d65a-fcd3-4d7d-a8f6-ee2af29bb429", name: "Mitsubishi" },
  { id: "9974d6e3-934a-44b4-9402-12b217a110a7", name: "Mitsubishi delica" },
  { id: "9ac4dce8-3687-4c35-b162-895622b0ffe5", name: "MITSUBISHI" },
  { id: "9b6fdac4-3084-417c-8769-1c44bb0971f6", name: "MOTORCYCLE" },
  { id: "98a75840-4e4c-4028-acf4-e735daed1ed4", name: "MUOIUER M/Cycle" },
  { id: "98a75840-f93a-4999-945f-71d3ea001b32", name: "Mazda" },
  { id: "99cf4263-3832-4fbb-b137-260a7b8f4472", name: "Motorcycle Super" },
  { id: "9a0437d9-2b14-4106-943a-2b9ac81c4d7f", name: "NASARA MOTORCYCLE" },
  { id: "9a940e47-5622-4dda-b0f8-924f945885dc", name: "NINO" },

  { id: "9c28a3fb-5a3d-4689-ae2e-1b06182fb514", name: "Navister" },

  { id: "98a7583e-d20f-4d28-aeed-07d33627f3f6", name: "Oldsmobile" },
  { id: "98a7583f-7d1c-469c-8d66-f8052e16e560", name: "Opel" },
  { id: "9950b96d-5c94-4643-9a7b-2a39819bfbea", name: "Opel" },
  { id: "9baa173e-4fdd-4e56-a46c-e449d05e45d4", name: "P P M CRANE." },
  { id: "9baa17b8-fc17-4484-94f3-d8c93d950a21", name: "P P M CRANE." },
  { id: "9a4dbeee-8fcd-4de2-b071-886d85c4db8b", name: "PAJERO" },
  { id: "9bbbd8d3-43e3-4ddf-904c-77cf4b642060", name: "PILGRIM" },
  { id: "9a82577f-46c4-430d-8e06-73300a6b3033", name: "PRADO" },
  { id: "9b45b66f-bd73-40f9-9331-379169be89fd", name: "Paggio" },
  { id: "98a7583f-d28e-48e1-9c9d-dd37668eae41", name: "Pontiac" },
  { id: "98a7583f-26b4-4a05-b885-1b520bfd8728", name: "Porsche" },
  { id: "99d4fdaa-a83c-47a2-be98-afaf7ff38c18", name: "QINGQI" },
  { id: "9b7be5ab-b454-4ee3-bcf3-6ce918d816e7", name: "QINGQI" },
  { id: "9c289a27-8035-4be9-b453-0aa9573777f0", name: "QIPAI" },
  { id: "99664182-6d52-4e22-a243-093c7c774292", name: "QLINK" },
  { id: "9976e8af-c75a-423a-98c8-7fbcbc874a49", name: "QLINK CG" },
  { id: "9976bba7-27a2-4aec-9bdb-634799d69c6a", name: "QLINK SPRINT" },
  { id: "98a75841-eed4-48ae-8548-b53879d843a4", name: "Qlink" },
  { id: "9c1a74b0-a122-420e-83b6-982103687e66", name: "RAM" },
  { id: "a02d0915-6016-4075-860e-ff4705ac2d47", name: "RANGE" },
  { id: "9a01e1a8-ed94-454a-a71a-3dbf600900fa", name: "RANGLER" },
  { id: "98f7c570-0df5-4888-9c7a-e102bc8322ea", name: "RENAULT" },
  { id: "9c1c9281-a9c0-4547-a049-79e0c1dd5909", name: "RENAULT" },
  { id: "99af18b2-dc42-444b-9d6f-15667f973a5e", name: "ROVER" },
  { id: "98a7583f-b6d0-4240-b9cf-9e2487462883", name: "Range Rover" },
  { id: "98a75843-68e5-49a4-a5bd-d5fb874963d3", name: "Renault" },
  { id: "99b13d37-c629-473c-b1fa-d5f12d631694", name: "Renault" },
  { id: "9b8db96d-2f72-4ed5-9e98-fdfc42667940", name: "Renault" },
  { id: "98a7583f-4a60-4f92-bb91-fcc87e09a7c3", name: "Rover" },
  { id: "9b9a2fcd-d03b-47ce-842f-46f7888f1f32", name: "SAAB" },
  { id: "99af595a-56e5-4029-9d15-3a60728ab432", name: "SALTI MOTORCYCLE" },
  { id: "9992ec66-9280-4618-96df-3fb29b2d99fe", name: "SAMSUNG" },
  { id: "997acc44-65ef-4dd4-a23e-e3ccc49e53e2", name: "SANY" },
  { id: "99a33f97-fb96-48f6-9379-7b3ac5e85e26", name: "SANYANG" },
  { id: "9a96828d-fe8b-4acd-bfe0-fc465733839e", name: "SCANIA" },
  { id: "9a29b801-16cd-4266-8769-5098ce888701", name: "SEAT" },
  { id: "9a29b896-f3f6-4c34-bbb1-3cd2c18bdd96", name: "SEAT" },
  { id: "9ab8d0c5-1b8d-4db7-83e4-2914753daf28", name: "SEAT" },
  { id: "9a0f9751-0707-4cfa-be30-bcc8046b62f7", name: "SENKE MOTORCYCLE" },
  { id: "9a805fc2-6b81-41d5-af77-7881d7e00ed4", name: "SHACMAN" },
  { id: "9a6a3740-fea7-4be2-993e-c5ef4d1b4592", name: "SHACMAN LARRY" },
  { id: "99c70361-6519-4d8e-8550-c2115c056d3b", name: "SIMBA" },
  { id: "99c6f626-6bbf-4a2b-8db0-85a736efe0bd", name: "SIMBA MOTORCYCLE" },
  { id: "98a75840-c471-4070-a576-39d5a704edfd", name: "SINO" },
  { id: "9a661f2b-6dbb-4c75-906b-b38e0fe188df", name: "SKODA" },
  { id: "98a75840-8483-4034-95d6-398049a9a56e", name: "SONLINK MOTORCYCLE" },
  { id: "9bb5f583-6431-462f-a3b2-a3a2b590cbb1", name: "SPACE-STAR" },
  { id: "994ec91f-18f7-43e2-bcde-5147a3b6571e", name: "SPRINTER" },
  { id: "99122434-6a8f-48d0-b2a7-3f3eb6871d7e", name: "SUPER Gallant" },
  { id: "98a7583e-e012-4228-877c-ccff48bacdb8", name: "SUPRA MARS" },
  { id: "9964d6c1-3444-4568-87f2-03e342409210", name: "SUZUKI" },
  { id: "9c0a79cc-4b79-47ef-96bb-fcc8f9da90f3", name: "SYM" },
  { id: "9a4a1f1a-4a97-4f59-a22a-09c9898adf37", name: "Sanyang" },
  { id: "98a7583f-cfbd-4fd6-8dad-300cf7687b6a", name: "Saturn" },
  { id: "9b7e0ee9-c251-4800-bbac-42a2179fbee7", name: "Savanna" },
  { id: "98a75845-11f2-4259-9e66-b516808ec760", name: "Scania" },
  { id: "98a7583f-ee69-4483-9ef4-3c92ef260f4a", name: "Scion" },
  { id: "98a7583f-5f15-40bc-bc7d-ee817492ec74", name: "Seat" },
  { id: "99af1760-cf6b-454b-88c8-a2b920808328", name: "Shacman" },
  { id: "9a114437-504e-499c-a2b5-b0da5bc1031a", name: "Shacman" },
  { id: "9a1a29da-e95d-4558-a438-91d63d6fd37b", name: "Shacman" },
  { id: "98a75846-340c-4237-ab05-90f176a07c3c", name: "Skoda" },
  { id: "98a75840-7211-4499-a9eb-68087c808c08", name: "Skygo" },
  { id: "9b49b12c-0591-4875-b9b7-e8abe4d8199d", name: "Smart" },
  {
    id: "9994dad9-15f2-499a-9e0d-87e3d4f030ac",
    name: "Son link motor cycle",
  },
  { id: "9a744173-d1ad-4336-b7d6-88adb548a8d9", name: "Ssangyong" },
  { id: "98a75845-53c8-48ce-9de7-60a3b3196206", name: "Sterling" },
  { id: "98a75844-5336-49fb-9de5-24ea98d0cf2b", name: "Steyr" },
  { id: "98a7583e-ac8f-418c-a2cf-b4df7ca5a5b4", name: "Subaru" },
  { id: "98a7583f-2315-4c9c-a0a9-81fb4b5a111a", name: "Suzuki" },
  { id: "99dd94a7-1ba4-48d0-80c3-85800d37d1c8", name: "Suzuki" },
  { id: "9b03066f-ff30-4b1a-9f30-fb34be15583f", name: "TACUMA" },
  { id: "98a75842-e822-455d-9d1f-d9533b87b722", name: "TATA" },
  { id: "9988f22a-7d09-4604-b24b-e41db3ba07b0", name: "TATA" },
  { id: "99b0fae9-f1b7-45e1-86da-1b4fe6c092bf", name: "TATA 407" },
  { id: "991226dc-226f-429e-8134-97edda8089cf", name: "TOPSTAR" },
  { id: "99508acd-ebfe-4f1f-a772-7552ad088c07", name: "TRACTOR" },
  { id: "98a75846-1db7-4c42-9b19-4da8a7aa4abd", name: "TVS" },
  { id: "999687b8-d256-414e-929c-2a493f5e2739", name: "TVS" },
  { id: "9b7fe595-2f96-4261-a472-d400f3de948f", name: "TVS" },
  { id: "999cce14-57d0-4f71-9138-a889863469d2", name: "TVS MOTORCYCLE" },
  { id: "999cd6f0-ce72-41fb-b379-895fa6b785a4", name: "TVS MOTORCYCLE" },
  { id: "98a75843-d980-458a-912b-54442b0fecca", name: "Tiger" },
  { id: "98a75843-0ccb-4e3e-8a18-aa00a5c5e4ca", name: "Twinco Motorcycle" },
  { id: "98a75841-6527-4517-a2a4-c38843abb039", name: "Vauxhall" },

  { id: "9ab67a05-bb97-42d8-af1b-5cdac1da1947", name: "WAGON" },
  { id: "9b9e02f3-d4c9-4cc6-b14c-b7ffae2627de", name: "WORKER" },
  { id: "9bd86caf-f4d2-4298-bed2-64cda595177e", name: "WRANGLE" },
  { id: "9968accc-4173-49c5-bea2-5530bfb73c98", name: "WRANGLER" },
  { id: "9b2fb04a-9b09-4314-8c19-2a188ce07b27", name: "WRANGLER" },
  { id: "9bb5e2ea-9fef-40a7-bcf2-7a7e253e8c82", name: "WRANGLER" },
  { id: "9d84c93d-541c-4e61-8266-cb67b3b8f9a5", name: "WRANGLER" },
  { id: "99972809-7149-4137-92dd-2130532026a1", name: "Wrangler" },
  { id: "9b0f4cb9-d749-429c-b47b-108980010980", name: "XCMG" },
  { id: "9b0f4cef-ce34-4754-96d4-a6acae40e98f", name: "XCMG" },
  { id: "9b0f4cfe-401f-4270-bc88-5fd406fec0dd", name: "XCMG" },
  { id: "9976e8cf-8c9e-4e78-8e8e-3823981a3114", name: "YAMAHA" },
  { id: "9930e09d-cda9-4b36-9f53-1175a34ffd93", name: "YAMAHA MOTORCYCLE" },
  { id: "98a75840-627a-415c-b36e-24ae80bcb0d7", name: "Yugo" },
  { id: "9a481572-be94-4738-bdc1-d011b5f2c0f7", name: "ZAHAV" },
  { id: "99342084-eb63-4a6b-afbb-6adddbeb2af9", name: "ZOTYE" },
  { id: "9a29b328-cace-496d-b6e0-d857e99cfa2e", name: "ZUZUKI" },
  { id: "99e9b769-d029-4b3c-9b2c-be473d9e0c9d", name: "dump" },
  { id: "9a85faa6-50e9-499e-b1b8-354244b2fd10", name: "jinbei" },
  { id: "9992d733-47db-447f-b502-bdd75162963e", name: "motorcycle Boxer" },
  { id: "99e5736a-a052-47ad-b854-ef2c31c4925d", name: "shacman" },
  { id: "9ae0ff6a-a14f-4a2c-af5d-7b5397cf1db5", name: "shacman" },
];

export const VEHICLE_COLORS = [
  { vehicleColorId: 18, colorName: "Ash", colorCode: "20" },
  { vehicleColorId: 21, colorName: "Black", colorCode: "1004" },
  { vehicleColorId: 17, colorName: "Blue", colorCode: "19" },
  { vehicleColorId: 6, colorName: "Brown", colorCode: "7" },
  { vehicleColorId: 15, colorName: "Commercial", colorCode: "16" },
  { vehicleColorId: 12, colorName: "Corporate", colorCode: "13" },
  { vehicleColorId: 16, colorName: "Cream", colorCode: "18" },
  { vehicleColorId: 13, colorName: "Custom", colorCode: "14" },
  { vehicleColorId: 14, colorName: "Gold", colorCode: "15" },
  { vehicleColorId: 8, colorName: "Green", colorCode: "9" },
  { vehicleColorId: 7, colorName: "Grey", colorCode: "8" },
  { vehicleColorId: 10, colorName: "Indigo", colorCode: "11" },
  { vehicleColorId: 9, colorName: "Orange", colorCode: "10" },
  { vehicleColorId: 22, colorName: "Other", colorCode: "1005" },
  { vehicleColorId: 3, colorName: "Pink", colorCode: "4" },
  { vehicleColorId: 20, colorName: "Purple", colorCode: "23" },
  { vehicleColorId: 1, colorName: "Red", colorCode: "1" },
  { vehicleColorId: 2, colorName: "Silver", colorCode: "3" },
  { vehicleColorId: 11, colorName: "Violet", colorCode: "12" },
  { vehicleColorId: 4, colorName: "White", colorCode: "5" },
  { vehicleColorId: 19, colorName: "Wine", colorCode: "22" },
  { vehicleColorId: 5, colorName: "Yellow", colorCode: "6" },
];

export const COMMERCIAL_TYPES = [
  "mini bus",
  "mini trucks",
  "suv",
  "commercial cars",
  "pickup",
];

export const YEAR_RANGE = {
  start: 1990,
  end: 2025,
};
